import { CopyOutlined, EditOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Segmented, Skeleton, Tag, message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { formatBytes } from '../../ui/bytesFormat'
import { useMembershipFlow } from '../contentProduction/canvas/membership/membershipFlowContext'
import { getTeamCreditTrend, getTeamOverview, teamErrorMessage } from './teamApi'
import type { CreditTrend, TeamOverview } from './teamTypes'
import { TeamAvatar, useTeamFormat } from './teamUi'

/**
 * VistoeraTASK-00525 — 团队概览. Every number comes from `/team/overview` (real
 * aggregates over the live Workspace); anything without a trustworthy source is
 * shown as 暂无数据 instead of being invented. The trend is a separate lazy call.
 */

const DONUT_COLORS: Record<string, string> = {
  image: 'var(--tm-accent)',
  video: '#8b5cf6',
  text: '#22c55e',
  audio: '#f59e0b',
  other: '#94a3b8',
}

function Trend({ data, num }: { data: CreditTrend; num: (v: string) => string }) {
  const { t } = useTranslation('team')
  const values = data.points.map((p) => Number(p.credits) || 0)
  const max = Math.max(...values, 0)
  if (!data.points.length || max <= 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('overview.noData')} />
  }
  const W = 560
  const H = 180
  const padL = 8
  const padB = 22
  const padT = 10
  const stepX = data.points.length > 1 ? (W - padL * 2) / (data.points.length - 1) : 0
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / max)
  const pts = values.map((v, i) => [padL + i * stepX, y(v)] as const)
  const line = pts.map(([x, yy], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${yy.toFixed(1)}`).join(' ')
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${H - padB} L${pts[0][0].toFixed(1)},${H - padB} Z`
  const labelEvery = Math.ceil(data.points.length / 7)
  return (
    <svg className="tm-trend-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('overview.trend.title')}>
      <path d={area} className="tm-trend-area" />
      <path d={line} className="tm-trend-line" />
      {pts.map(([x, yy], i) => (
        <g key={data.points[i].date}>
          <circle cx={x} cy={yy} r={3} className="tm-trend-dot">
            <title>{`${data.points[i].date}: ${num(data.points[i].credits)}`}</title>
          </circle>
          {i % labelEvery === 0 && (
            <text x={x} y={H - 6} textAnchor="middle" className="tm-trend-label">
              {data.points[i].date.slice(5)}
            </text>
          )}
        </g>
      ))}
    </svg>
  )
}

function Donut({ items, total }: { items: TeamOverview['distribution']['items']; total: number }) {
  const R = 52
  const C = 2 * Math.PI * R
  let offset = 0
  return (
    <svg className="tm-donut-svg" viewBox="0 0 140 140" role="img">
      <circle cx="70" cy="70" r={R} fill="none" stroke="var(--tm-border)" strokeWidth="16" />
      {items.map((it) => {
        const len = total > 0 ? (it.count / total) * C : 0
        const el = (
          <circle
            key={it.key}
            cx="70"
            cy="70"
            r={R}
            fill="none"
            stroke={DONUT_COLORS[it.key] ?? DONUT_COLORS.other}
            strokeWidth="16"
            strokeDasharray={`${len} ${C - len}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 70 70)"
          />
        )
        offset += len
        return el
      })}
    </svg>
  )
}

export default function TeamOverviewTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('team')
  const navigate = useNavigate()
  const { openMembership } = useMembershipFlow()
  const { num, dateTime } = useTeamFormat()
  const [data, setData] = useState<TeamOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState<7 | 14 | 30>(14)
  const [trend, setTrend] = useState<CreditTrend | null>(null)
  const [trendError, setTrendError] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getTeamOverview(workspaceId)
      setData(res.data)
    } catch (e) {
      setError(teamErrorMessage(e, t('overview.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, t])

  useEffect(() => {
    void load()
  }, [load])

  // Lazy trend: only once the overview itself is on screen, and again per range change.
  useEffect(() => {
    if (!data) return
    let cancelled = false
    setTrendError(false)
    getTeamCreditTrend(workspaceId, days)
      .then((res) => !cancelled && setTrend(res.data))
      .catch(() => !cancelled && setTrendError(true))
    return () => {
      cancelled = true
    }
  }, [workspaceId, days, data])

  const base = `/workspace/${workspaceId}/team`
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(workspaceId)
      void message.success(t('overview.team.copied'))
    } catch {
      void message.error(t('overview.team.copyFailed'))
    }
  }

  const fnLabel = useCallback(
    (key: string): string => {
      // mirrors the backend task-type classification (canonical operation_key namespace)
      if (key.startsWith('image.') || key.startsWith('tryon.')) return t('overview.fn.image')
      if (key.startsWith('video.generate') || key.startsWith('motion.')) return t('overview.fn.video')
      if (key.startsWith('audio.')) return t('overview.fn.audio')
      if (key.startsWith('ai.') || key.startsWith('content.') || key.startsWith('reference.') || key.startsWith('ip.') || key.startsWith('video.'))
        return t('overview.fn.text')
      return key
    },
    [t],
  )

  // The API ranks raw operation keys; several keys share one friendly label
  // (e.g. every `ai.*` call), so merge by label before showing a top list.
  const topFns = useMemo(() => {
    const merged = new Map<string, number>()
    for (const f of data?.top_functions ?? []) {
      const label = fnLabel(f.key)
      merged.set(label, (merged.get(label) ?? 0) + f.count)
    }
    return [...merged.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .slice(0, 5)
  }, [data, fnLabel])

  if (loading && !data) return <Skeleton active paragraph={{ rows: 10 }} />
  if (error && !data) {
    return (
      <Alert
        type="error"
        showIcon
        title={error}
        action={
          <Button size="small" onClick={() => void load()}>
            {t('common.retry')}
          </Button>
        }
      />
    )
  }
  if (!data) return null

  const dist = data.distribution
  const metrics = data.metrics

  return (
    <div className="tm-overview" data-testid="team-overview">
      <div className="tm-row tm-row-top">
        <section className="tm-card tm-team-card">
          <TeamAvatar src={data.team.avatar_data_url} name={data.team.name} size={72} />
          <div className="tm-team-main">
            <div className="tm-team-name-row">
              <h2 className="tm-team-name" data-testid="team-name">
                {data.team.name}
              </h2>
              <span className="tm-pill">{t('overview.team.badge')}</span>
            </div>
            <div className="tm-kv">
              <span className="tm-kv-k">{t('overview.team.id')}</span>
              <code className="tm-kv-v">{data.team.workspace_id}</code>
              <button type="button" className="tm-icon-btn" onClick={() => void copyId()} aria-label={t('overview.team.copy')}>
                <CopyOutlined />
              </button>
            </div>
            <div className="tm-kv-row">
              <div className="tm-kv">
                <span className="tm-kv-k">{t('overview.team.createdAt')}</span>
                <span className="tm-kv-v">{dateTime(data.team.created_at)}</span>
              </div>
              <div className="tm-kv">
                <span className="tm-kv-k">{t('overview.team.members')}</span>
                <span className="tm-kv-v">{t('overview.team.memberCount', { count: data.team.member_total })}</span>
              </div>
            </div>
          </div>
          <Button icon={<EditOutlined />} onClick={() => navigate(`${base}/settings`)} data-testid="team-edit-info">
            {t('overview.team.edit')}
          </Button>
        </section>

        <section className="tm-card tm-plan-card" data-testid="team-plan-card">
          <span className="tm-card-label">{t('overview.plan.title')}</span>
          <div className="tm-plan-name" data-testid="team-plan-name">
            {data.plan?.name || t('overview.plan.unknown')}
          </div>
          <div className="tm-kv-row">
            <div className="tm-kv">
              <span className="tm-kv-k">{t('overview.plan.status')}</span>
              <Tag color={data.plan?.status === 'active' ? 'green' : 'default'} data-testid="team-plan-status">
                {data.plan?.status || t('overview.noData')}
              </Tag>
            </div>
            <div className="tm-kv">
              <span className="tm-kv-k">{t('overview.plan.expires')}</span>
              <span className="tm-kv-v" data-testid="team-plan-expires">
                {data.plan?.expires_at ? dateTime(data.plan.expires_at) : t('overview.plan.noExpiry')}
              </span>
            </div>
          </div>
          <div className="tm-kv">
            <span className="tm-kv-k">{t('overview.plan.credits')}</span>
            <span className="tm-kv-v" data-testid="team-plan-credits">
              {data.plan?.credit_balance == null ? t('overview.noData') : num(data.plan.credit_balance)}
            </span>
          </div>
          <div className="tm-kv">
            <span className="tm-kv-k">{t('overview.plan.storage')}</span>
            <span className="tm-kv-v" data-testid="team-plan-storage">
              {data.plan?.storage_quota_bytes == null
                ? t('overview.noData')
                : formatBytes(data.plan.storage_quota_bytes)}
            </span>
          </div>
        </section>

        <section className="tm-card tm-credit-card">
          <div className="tm-credit-head">
            <span className="tm-card-label">{t('overview.credits.title')}</span>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={openMembership} data-testid="team-buy-credits">
              {t('overview.credits.buy')}
            </Button>
          </div>
          <div className="tm-credit-balance" data-testid="team-credit-balance">
            {data.credits.balance === null ? t('overview.noData') : num(data.credits.balance)}
          </div>
          <div className="tm-credit-sub">
            {t('overview.credits.monthUsed')}
            <strong>{num(data.credits.month_used)}</strong>
          </div>
          <div className="tm-hint">{t('overview.credits.sharedPool')}</div>
        </section>
      </div>

      <div className="tm-row tm-row-metrics">
        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('overview.metrics.members')}</span>
          <strong className="tm-metric-value">{num(metrics.member_total)}</strong>
          <button type="button" className="tm-link" onClick={() => navigate(`${base}/members`)}>
            {t('overview.metrics.manage')}
          </button>
        </div>
        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('overview.metrics.creditsUsed')}</span>
          <strong className="tm-metric-value">{num(metrics.month_credits_used)}</strong>
          <span className="tm-hint">{t('overview.metrics.thisMonth')}</span>
        </div>
        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('overview.metrics.images')}</span>
          <strong className="tm-metric-value">{num(metrics.month_images)}</strong>
          <span className="tm-hint">{t('overview.metrics.thisMonth')}</span>
        </div>
        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('overview.metrics.videos')}</span>
          <strong className="tm-metric-value">{num(metrics.month_videos)}</strong>
          <span className="tm-hint">{t('overview.metrics.thisMonth')}</span>
        </div>
        <div className="tm-card tm-metric">
          <span className="tm-card-label">{t('overview.metrics.aiChats')}</span>
          <strong className="tm-metric-value">{num(metrics.month_ai_chats)}</strong>
          <span className="tm-hint">{t('overview.metrics.thisMonth')}</span>
        </div>
      </div>

      <div className="tm-row tm-row-charts">
        <section className="tm-card tm-trend-card">
          <div className="tm-card-head">
            <span className="tm-card-title">{t('overview.trend.title')}</span>
            <Segmented<7 | 14 | 30>
              size="small"
              value={days}
              onChange={setDays}
              options={[
                { value: 7, label: t('overview.trend.d7') },
                { value: 14, label: t('overview.trend.d14') },
                { value: 30, label: t('overview.trend.d30') },
              ]}
            />
          </div>
          {trendError ? (
            <Alert type="warning" showIcon title={t('overview.trend.failed')} />
          ) : trend ? (
            <Trend data={trend} num={num} />
          ) : (
            <Skeleton active paragraph={{ rows: 4 }} title={false} />
          )}
        </section>

        <section className="tm-card tm-dist-card">
          <div className="tm-card-head">
            <span className="tm-card-title">{t('overview.dist.title')}</span>
          </div>
          {dist.total > 0 ? (
            <div className="tm-dist-body">
              <Donut items={dist.items} total={dist.total} />
              <ul className="tm-legend">
                {dist.items.map((it) => (
                  <li key={it.key}>
                    <span className="tm-dot" style={{ background: DONUT_COLORS[it.key] ?? DONUT_COLORS.other }} />
                    <span>{t(`overview.dist.${it.key}`)}</span>
                    <strong>{Math.round((it.count / dist.total) * 100)}%</strong>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('overview.noData')} />
          )}
        </section>

        <section className="tm-card tm-top-card">
          <div className="tm-card-head">
            <span className="tm-card-title">{t('overview.top.title')}</span>
          </div>
          {topFns.length ? (
            <ol className="tm-top-list">
              {topFns.map((f, i) => (
                <li key={f.label}>
                  <span className="tm-rank">{i + 1}</span>
                  <span className="tm-top-name">{f.label}</span>
                  <strong>{t('overview.top.times', { count: f.count })}</strong>
                </li>
              ))}
            </ol>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('overview.noData')} />
          )}
        </section>
      </div>

      <section className="tm-card tm-activity-card">
        <div className="tm-card-head">
          <span className="tm-card-title">{t('overview.activity.title')}</span>
        </div>
        {data.recent_activity.length ? (
          <ul className="tm-activity">
            {data.recent_activity.map((ev) => (
              <li key={ev.id}>
                <span className="tm-activity-text">
                  {t(`activity.${ev.action}`, {
                    defaultValue: ev.action,
                    actor: ev.actor_name ?? t('activity.unknown'),
                    target: ev.target_name ?? t('activity.unknown'),
                  })}
                  {ev.result === 'rejected' && <span className="tm-activity-fail"> · {t('activity.rejected')}</span>}
                </span>
                <span className="tm-activity-time">{dateTime(ev.created_at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('overview.activity.empty')} />
        )}
      </section>
    </div>
  )
}
